import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";
import "./Login.css";
import { ROLE_SIDEBAR_CONFIG } from "../components/shared/roleSidebarConfig";
import {
    getCaregiverProfileSetupHint,
    resolveCaregiverPostAuthRoute,
} from "../utils/caregiverJourney";
import {
    normalizeIndianMobile,
    sanitizeLoginIdInput,
    sanitizeMobileDigits,
} from "../utils/indianIdInput";
import { OTP_LENGTH, STUB_OTP, isValidOtpInput, sanitizeOtpInput } from "../utils/otpCode";
import { usePlatformConfig } from "../context/PlatformConfigContext";
import { formatUserFacingError, notifyUserFacingError, USER_FACING_ERRORS } from "../utils/userFacingErrors";
import { showToast } from "../utils/toast";
import { isAdminPath, isOpsAuthRole } from "../utils/routeAccess";
import {
    AUTH_ACTIONS,
    buildAuthOverlayUrl,
    buildPostCustomerAuthNavigation,
    clearAuthOverlayParams,
    resolveAuthAction,
    resolvePublicOverlayPath,
    shouldRedirectLoginToOverlay,
} from "../utils/authNavigation";
import CustomerHireOnboarding from "../components/customer/CustomerHireOnboarding";
import LocationAutocomplete from "../components/shared/LocationAutocomplete";
import { formatExactLocationLabel, resolveIndianCity, resolveNearestIndianCity } from "../config/indianCities";
import { CUSTOMER_SIGNUP_LEAD_SOURCES, CAREGIVER_SIGNUP_LEAD_SOURCES } from "../config/leadCrmConfig";
import {
    getFirstTouchAttribution,
    mapAttributionToLeadSource,
    clearFirstTouchAttribution,
} from "../utils/attributionCapture";
import { trackPixelEvent } from "../services/metaPixel";
import { trackGa4Event } from "../services/ga4";
import { CARE_TYPES } from "../config/marketplaceFilterEngine";
import { CARE_MANAGER_TERMS_VERSION } from "../config/careManagerCompleteProfile";
import { SIGNUP_STEP_TITLES } from "../config/careManagerRegistration";
import CareManagerSignupFields from "../components/auth/CareManagerSignupFields";
import {
  INIT_MANAGER_SIGNUP,
  MANAGER_SIGNUP_TOTAL_STEPS,
  normalizeCmPin,
  validateManagerSignupStep,
} from "../utils/careManagerSignupValidation";
import { normalizeGender } from "../utils/genderNormalize";
import { reverseGeocodeAddress } from "../utils/reverseGeocode";
import { isUsableProfileName } from "../utils/customerJourney";

const BOOKING_DRAFT_KEY = "careHubBookingDraft";

/**
 * A draft stamped with a different account's ownerUserId belongs to whoever was
 * previously signed in on this browser/tab — never resume it into a new login.
 * Guest drafts (ownerUserId absent, created before any login) still resume as before.
 */
function draftBelongsToUser(draft, currentUserId) {
    if (!draft || draft.ownerUserId == null) return true;
    return Number(draft.ownerUserId) === Number(currentUserId);
}

function hasPendingCareHubBookingDraft(currentUserId = null) {
    try {
        const raw = sessionStorage.getItem(BOOKING_DRAFT_KEY);
        if (!raw) return false;
        const draft = JSON.parse(raw);
        if (!draft?.caregiverId) return false;
        if (!draftBelongsToUser(draft, currentUserId)) {
            sessionStorage.removeItem(BOOKING_DRAFT_KEY);
            return false;
        }
        return Boolean(
            draft.resumeAfterLogin ||
                draft.resumeToPayment ||
                draft.resumeAfterCarePass ||
                draft.selectedSlot
        );
    } catch {
        return false;
    }
}

function navigateResumeCareHubBooking(navigate, { loginMessage } = {}) {
    let draft = null;
    try {
        draft = JSON.parse(sessionStorage.getItem(BOOKING_DRAFT_KEY) || "null");
    } catch {
        draft = null;
    }
    const caregiverId = draft?.caregiverId;
    if (!caregiverId) {
        navigate("/marketplace", { replace: true, state: { loginMessage } });
        return;
    }
    navigate("/marketplace", {
        replace: true,
        state: {
            bookingResume: true,
            loginMessage:
                loginMessage || "Welcome back. Continue your booking where you left off.",
            openHireCaregiver: {
                id: caregiverId,
                caregiverId,
                name: draft.caregiverName || "Caregiver",
                isQualified: true,
            },
        },
    });
}

const CAREGIVER_SIGNUP_SERVICE_ROLES = Object.values(CARE_TYPES).filter(
    (type) => type.id !== "dementia" && type.id !== "post_operative"
);

const CUSTOMER_LEAD_SOURCE_UI_IDS = [
  "WHATSAPP",
  "GOOGLE_ADS",
  "INSTAGRAM",
  "FACEBOOK_ADS",
  "REFERRAL_PROGRAM",
  "DIRECT_WEBSITE",
  "OTHER",
];

const LEAD_SOURCE_OPTIONS = CUSTOMER_SIGNUP_LEAD_SOURCES
  .filter((opt) => CUSTOMER_LEAD_SOURCE_UI_IDS.includes(opt.id))
  .map((opt) => {
    const mappedLabel =
      opt.id === "WHATSAPP"
        ? "Whatsapp"
        : opt.id === "GOOGLE_ADS"
          ? "google"
          : opt.id === "INSTAGRAM"
            ? "instagram"
            : opt.id === "FACEBOOK_ADS"
              ? "Facebook"
              : opt.id === "REFERRAL_PROGRAM"
                ? "refer by someone"
                : opt.id === "DIRECT_WEBSITE"
                  ? "visiting directly"
                  : opt.id === "OTHER"
                    ? "others"
                    : opt.label;
    return { ...opt, label: mappedLabel };
  })
  .sort((a, b) => CUSTOMER_LEAD_SOURCE_UI_IDS.indexOf(a.id) - CUSTOMER_LEAD_SOURCE_UI_IDS.indexOf(b.id));

const CAREGIVER_LEAD_SOURCE_OPTIONS = CAREGIVER_SIGNUP_LEAD_SOURCES.map((opt) => {
  const mappedLabel =
    opt.id === "WHATSAPP"
      ? "Whatsapp"
      : opt.id === "GOOGLE_ADS"
        ? "google"
        : opt.id === "INSTAGRAM"
          ? "instagram"
          : opt.id === "FACEBOOK_ADS"
            ? "Facebook"
            : opt.id === "REFERRAL_PROGRAM"
              ? "refer by someone"
              : opt.id === "CAREGIVER_REFERRAL"
                ? "another caregiver referred me"
                : opt.id === "DIRECT_WEBSITE"
                  ? "visiting directly"
                  : opt.id === "OTHER"
                    ? "others"
                    : opt.label;
  return { ...opt, label: mappedLabel };
});

const IS_DEV = process.env.NODE_ENV === "development";

const PASSWORD_MIN = 8;
const CREATE_PASSWORD_MIN = 8;

const ROLES = [
    { id: "customer", title: "Customer", icon: "👨‍👩‍👧" },
    { id: "caregiver", title: "Caregiver", icon: "🧑‍⚕️" },
    { id: "manager", title: "Care Manager", icon: "📋" },
];

const ROLE_SUB_SIGNIN = { customer: "Book care", caregiver: "Manage shifts", manager: "Oversee cases" };
const ROLE_SUB_CREATE = { customer: "Find a caregiver", caregiver: "Join marketplace", manager: "List your services" };

const DEFAULT_COORDS = { latitude: "12.971600", longitude: "77.594600" };
const OTP_COUNTDOWN_SECONDS = 60;

function formatOtpTimer(seconds) {
    const s = Math.max(0, Number(seconds) || 0);
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
}

function validateEmail(value) {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return "Enter a valid email address.";
    return "";
}

function roleToUserType(role) {
    if (role === "caregiver") return "CAREGIVER";
    if (role === "manager") return "CARE_MANAGER";
    if (role === "customer") return "CUSTOMER";
    return "";
}

/** Sign-in uses the account role stored on the server; ?role= is only for registration. */
function resolveLoginUserType({ isAdminLogin, mobile, selectedRole }) {
    if (isAdminLogin) {
        return roleToUserType(selectedRole) || (mobile ? undefined : "ADMIN");
    }
    if (selectedRole === "manager") {
        return "CARE_MANAGER";
    }
    return undefined;
}

function formatOtpSentMessage() {
    if (IS_DEV) return `Use OTP ${STUB_OTP} to verify (SMS disabled).`;
    return "We sent a verification code to your mobile. Enter it below.";
}

function validateLoginMobile(value) {
    const mobile = normalizeIndianMobile(value);
    if (mobile) return "";
    const trimmed = String(value || "").trim();
    if (trimmed.length >= 3) return "";
    return "Enter your 10-digit mobile or admin username.";
}

function validatePassword(value) {
    if (!value) return "Password is required.";
    if (value.length < PASSWORD_MIN) return `Password must be at least ${PASSWORD_MIN} characters.`;
    return "";
}

function handleOtpCodeChange(setter, next) {
    setter(sanitizeOtpInput(next, OTP_LENGTH));
}

function OtpDigitInput({ id, length = OTP_LENGTH, value, onChange, disabled = false }) {
    const digits = String(value || "")
        .replace(/\D/g, "")
        .slice(0, length)
        .padEnd(length, " ")
        .split("");

    function applyDigits(nextRaw) {
        onChange(String(nextRaw || "").replace(/\D/g, "").slice(0, length));
    }

    function handleChange(index, char) {
        const sanitized = char.replace(/\D/g, "");
        const current = String(value || "").replace(/\D/g, "").slice(0, length);
        if (!sanitized) {
            applyDigits(current.slice(0, index) + current.slice(index + 1));
            return;
        }
        const next =
            current.slice(0, index) + sanitized.slice(-1) + current.slice(index + 1);
        applyDigits(next.slice(0, length));
        if (sanitized && index < length - 1) {
            const nextInput = document.getElementById(`${id}-${index + 1}`);
            nextInput?.focus();
        }
    }

    function handleKeyDown(index, e) {
        if (e.key === "Backspace" && !digits[index]?.trim() && index > 0) {
            const prevInput = document.getElementById(`${id}-${index - 1}`);
            prevInput?.focus();
        }
    }

    function handlePaste(e) {
        e.preventDefault();
        const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length);
        if (pasted) applyDigits(pasted);
    }

    return (
        <div className="login-otp-grid" role="group" aria-label="Verification code">
            {digits.map((digit, index) => (
                <input
                    key={`${id}-${index}`}
                    id={`${id}-${index}`}
                    className="login-otp-grid__cell"
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    value={digit.trim()}
                    disabled={disabled}
                    aria-label={`Digit ${index + 1} of ${length}`}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                />
            ))}
        </div>
    );
}

function PasswordVisibilityToggle({ visible, onToggle }) {
    return (
        <button
            type="button"
            className="login-form__password-toggle"
            onClick={onToggle}
            aria-label={visible ? "Hide password" : "Show password"}
            aria-pressed={visible}
        >
            {visible ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <path d="M1 1l22 22" />
                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                </svg>
            ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                </svg>
            )}
        </button>
    );
}

function validateCreateForm(
    values,
    {
        requireLocation = true,
        requireTerms = true,
        requireGender = false,
        requireRelationship = false,
        requirePassword = false,
        requireCity = false,
        requireServiceRole = false,
        requireName = true,
        requireCodeOfConduct = false,
    } = {}
) {
    const mobile = normalizeIndianMobile(values.mobile);
    const latitude = (values.latitude || "").trim();
    const longitude = (values.longitude || "").trim();
    const pwd = values.password || "";
    const errors = {
        fullName: "",
        firstName: "",
        lastName: "",
        email: "",
        gender: "",
        mobile: "",
        city: "",
        latitude: "",
        longitude: "",
        relationshipToSenior: "",
        password: "",
        agreeTerms: "",
        agreeCodeOfConduct: "",
        serviceRole: "",
    };

    if (requireName && !values.firstName.trim() && !values.fullName?.trim()) {
        errors.firstName = "Full name is required.";
        errors.fullName = "Full name is required.";
    } else if (
        requireName &&
        !isUsableProfileName(values.fullName || values.firstName)
    ) {
        errors.firstName = "Enter your full name (not your mobile number).";
        errors.fullName = "Enter your full name (not your mobile number).";
    }
    const emailErr = validateEmail(values.email);
    if (emailErr) errors.email = emailErr;
    if (!mobile) errors.mobile = "Enter a valid 10-digit mobile number.";
    if (requireGender && !values.gender?.trim()) errors.gender = "Please select your gender.";
    if (requireServiceRole && !values.serviceRole?.trim()) {
        errors.serviceRole = "Please select your service type.";
    }
    if (requireRelationship && !values.relationshipToSenior?.trim()) {
        errors.relationshipToSenior = "Please select your relationship to the patient.";
    }
    if (requireLocation) {
        if (!latitude || Number.isNaN(Number(latitude))) errors.latitude = "Valid latitude is required.";
        if (!longitude || Number.isNaN(Number(longitude))) errors.longitude = "Valid longitude is required.";
    }
    if (!values.city?.trim() && requireCity) {
        errors.city = "City or location is required.";
    }

    if (requirePassword) {
        if (!pwd) errors.password = "Password is required.";
        else if (pwd.length < CREATE_PASSWORD_MIN)
            errors.password = `Password must be at least ${CREATE_PASSWORD_MIN} characters.`;
        else if (
            !/[a-z]/.test(pwd) ||
            !/[A-Z]/.test(pwd) ||
            !/\d/.test(pwd) ||
            !/[^A-Za-z0-9]/.test(pwd)
        )
            errors.password = "Use upper, lower, number and special character.";
    }

    if (requireTerms && !values.agreeTerms)
        errors.agreeTerms = "Please accept Terms of Service and Privacy Policy.";

    if (requireCodeOfConduct && !values.agreeCodeOfConduct)
        errors.agreeCodeOfConduct = "Please accept the Care Manager Code of Conduct.";

    const isValid = Object.values(errors).every((v) => !v);
    return { errors, isValid, mobile };
}

const INIT_LOGIN = { mobile: "", password: "" };
const INIT_LOGIN_ERRORS = { mobile: "", password: "" };

const INIT_CREATE = {
    fullName: "",
    firstName: "",
    lastName: "",
    email: "",
    gender: "",
    mobile: "",
    city: "",
    relationshipToSenior: "",
    latitude: "",
    longitude: "",
    password: "",
    agreeTerms: false,
    agreeCodeOfConduct: false,
    serviceRole: "attendant",
    leadSource: "",
};
const INIT_CREATE_ERRORS = {
    fullName: "",
    firstName: "",
    lastName: "",
    email: "",
    gender: "",
    mobile: "",
    city: "",
    relationshipToSenior: "",
    latitude: "",
    longitude: "",
    password: "",
    agreeTerms: "",
    agreeCodeOfConduct: "",
    serviceRole: "",
};

function applyAuthError(err, setInline, fallback) {
    const msg = formatUserFacingError(err, fallback);
    setInline(msg);
    const code = String(err?.code || err?.body?.code || "").toUpperCase();
    if (USER_FACING_ERRORS[code] || Number(err?.status) === 503) {
        notifyUserFacingError(err, fallback);
    }
}

export default function Login({ asOverlay = false, onDismiss } = {}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { isFeatureEnabled, isMaintenanceMode, isRegistrationEnabled } = usePlatformConfig();

    const [showCreate, setShowCreate] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("tab") === "create";
    });
    const [selectedRole, setSelectedRole] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const role = params.get("role");
        if (role === "caregiver") return "caregiver";
        if (role === "customer") return "customer";
        if (role === "manager") return "manager";
        if (params.get("hire")) return "customer";
        if (params.get("tab") === "create" && !role) return "customer";
        return "";
    });
    const [roleError, setRoleError] = useState("");

    const [loginValues, setLoginValues] = useState(INIT_LOGIN);
    const [loginErrors, setLoginErrors] = useState(INIT_LOGIN_ERRORS);
    const [loginMessage, setLoginMessage] = useState("");
    const [loginError, setLoginError] = useState("");
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginStep, setLoginStep] = useState(1);
    const [loginOtpMobile, setLoginOtpMobile] = useState("");

    const [createValues, setCreateValues] = useState(INIT_CREATE);
    const [createErrors, setCreateErrors] = useState(INIT_CREATE_ERRORS);
    const [createMessage, setCreateMessage] = useState("");
    const [createError, setCreateError] = useState("");
    const [createLoading, setCreateLoading] = useState(false);
    const [geoLoading, setGeoLoading] = useState(false);
    const [geoMessage, setGeoMessage] = useState("");
    const locationChosenByUserRef = useRef(false);

    const [customerSignupStep, setCustomerSignupStep] = useState(1);
    const [customerHireOnboarding, setCustomerHireOnboarding] = useState(false);
    const [hireRegistrationMeta, setHireRegistrationMeta] = useState(null);
    const [caregiverSignupStep, setCaregiverSignupStep] = useState(1);
    const [managerSignupStep, setManagerSignupStep] = useState(1);
    const [managerForm, setManagerForm] = useState(INIT_MANAGER_SIGNUP);
    const [managerCustomArea, setManagerCustomArea] = useState("");
    const [managerTermsError, setManagerTermsError] = useState("");
    const [otpCode, setOtpCode] = useState("");
    const [otpSending, setOtpSending] = useState(false);
    const [otpMessage, setOtpMessage] = useState("");
    const [phoneOtpVerificationToken, setPhoneOtpVerificationToken] = useState("");
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [signupOtpSent, setSignupOtpSent] = useState(false);
    const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);

    const isHireFlow = Boolean(searchParams.get("hire"));
    const returnToPath = searchParams.get("returnTo");
    const isAdminLogin =
        searchParams.get("admin") === "1" ||
        isAdminPath(location.pathname) ||
        (returnToPath ? isAdminPath(returnToPath) : false);
    const showRolePicker =
        !isAdminLogin && !isHireFlow && showCreate && !selectedRole;

    useEffect(() => {
        if (!isAdminLogin || asOverlay) return;
        const token = api.getToken?.();
        const authRole = api.getAuthRoleFromToken?.();
        if (token && isOpsAuthRole(authRole)) {
            const dest =
                returnToPath && returnToPath.startsWith("/admin") ? returnToPath : "/admin";
            navigate(dest, { replace: true });
        }
    }, [asOverlay, isAdminLogin, navigate, returnToPath]);

    useEffect(() => {
        if (location.pathname === "/login") {
            const legacyAdmin =
                searchParams.get("admin") === "1" ||
                isAdminPath(searchParams.get("returnTo") || "");
            if (legacyAdmin) {
                const next = new URLSearchParams();
                const returnTo = searchParams.get("returnTo");
                if (returnTo && isAdminPath(returnTo)) next.set("returnTo", returnTo);
                const qs = next.toString();
                navigate(`/admin/login${qs ? `?${qs}` : ""}`, { replace: true });
                return;
            }
        }
    }, [location.pathname, navigate, searchParams]);

    useEffect(() => {
        const tab = searchParams.get("tab");
        const role = searchParams.get("role");
        if (isAdminLogin) {
            setShowCreate(false);
            return;
        }
        if (tab === "create") {
            setShowCreate(true);
            if (!role && !isHireFlow) setSelectedRole("customer");
        }
        if (isHireFlow && !role) setSelectedRole("customer");
        if (role === "caregiver") setSelectedRole("caregiver");
        else if (role === "customer") setSelectedRole("customer");
        else if (role === "manager") setSelectedRole("manager");
    }, [searchParams, isHireFlow, isAdminLogin]);

    function syncAuthOverlayParams({ tab, role } = {}) {
        if (isAdminLogin) return;
        const onOverlay = asOverlay || shouldRedirectLoginToOverlay(searchParams);
        if (!onOverlay) return;
        const next = clearAuthOverlayParams(searchParams);
        next.set("auth", "login");
        if (tab) next.set("tab", tab);
        if (role) next.set("role", role);
    const returnTo = searchParams.get("returnTo");
    if (returnTo) next.set("returnTo", returnTo);
    const hire = searchParams.get("hire");
    if (hire) next.set("hire", hire);
    const action = searchParams.get("action");
    if (action) next.set("action", action);
    const caregiverId = searchParams.get("caregiverId");
    if (caregiverId) next.set("caregiverId", caregiverId);
        const path = window.location.pathname || "/";
        navigate(`${path}?${next.toString()}`, { replace: true });
    }

    useEffect(() => {
        if (asOverlay || isAdminLogin) return;
        if (!shouldRedirectLoginToOverlay(searchParams)) return;
        const returnTo = searchParams.get("returnTo") || "/marketplace";
        const target = buildAuthOverlayUrl(resolvePublicOverlayPath(returnTo), {
            returnTo,
            hire: searchParams.get("hire"),
            tab: searchParams.get("tab") || "login",
            role: searchParams.get("role"),
        });
        navigate(target, { replace: true });
    }, [asOverlay, isAdminLogin, searchParams, navigate]);

    useEffect(() => {
        if (!asOverlay) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [asOverlay]);

    useEffect(() => {
        if (otpSecondsLeft <= 0) return undefined;
        const timer = window.setTimeout(() => setOtpSecondsLeft((s) => s - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [otpSecondsLeft]);

    useEffect(() => {
        if (!showCreate || selectedRole !== "customer" || isHireFlow) return;
        if (createValues.latitude && createValues.longitude) return;
        if (locationChosenByUserRef.current) return;
        detectLocation({ silent: true });
    }, [showCreate, selectedRole, isHireFlow]);

    useEffect(() => {
        if (!isHireFlow) return;
        const loc = searchParams.get("searchLocation");
        const lat = searchParams.get("lat");
        const lng = searchParams.get("lng");
        setCreateValues((prev) => ({
            ...prev,
            ...(loc && !prev.city ? { city: loc } : {}),
            ...(lat && !prev.latitude ? { latitude: lat } : {}),
            ...(lng && !prev.longitude ? { longitude: lng } : {}),
        }));
    }, [isHireFlow, searchParams]);

    useEffect(() => {
        if (!isHireFlow || !showCreate || selectedRole !== "customer" || customerHireOnboarding) return;
        const mobile = normalizeIndianMobile(createValues.mobile);
        if (!mobile || mobile.length !== 10 || signupOtpSent || otpSending) return;
        sendSignupOtp("CUSTOMER_SIGNUP");
    }, [
        createValues.mobile,
        isHireFlow,
        showCreate,
        selectedRole,
        signupOtpSent,
        otpSending,
        customerHireOnboarding,
    ]);

    const displayRoles = useMemo(() => {
        if (!showCreate) return ROLES;
        if (!isRegistrationEnabled()) return [];
        return ROLES.filter((role) => {
            if (role.id === "caregiver") {
                return isFeatureEnabled("caregiverSelfRegistrationEnabled");
            }
            return true;
        });
    }, [showCreate, isFeatureEnabled, isRegistrationEnabled]);

    useEffect(() => {
        if (isAdminLogin || !showCreate) return;
        if (!isRegistrationEnabled()) {
            showToast.info(USER_FACING_ERRORS.REGISTRATION_DISABLED.message);
        } else if (
            selectedRole === "caregiver" &&
            showCreate &&
            !isFeatureEnabled("caregiverSelfRegistrationEnabled")
        ) {
            showToast.info(USER_FACING_ERRORS.CAREGIVER_REGISTRATION_DISABLED.message);
        }
    }, [showCreate, isAdminLogin, isRegistrationEnabled, selectedRole, isFeatureEnabled]);


    function resetLoginFlow() {
        setLoginStep(1);
        setLoginOtpMobile("");
        setOtpCode("");
        setOtpMessage("");
    }

    function resetSignupFlow() {
        setCustomerSignupStep(1);
        setCaregiverSignupStep(1);
        setManagerSignupStep(1);
        setManagerForm(INIT_MANAGER_SIGNUP);
        setManagerCustomArea("");
        setManagerTermsError("");
        setOtpCode("");
        setOtpMessage("");
        setPhoneOtpVerificationToken("");
        setSignupOtpSent(false);
        setOtpSecondsLeft(0);
        locationChosenByUserRef.current = false;
    }

    function patchManagerForm(patch) {
        setManagerForm((prev) => ({ ...prev, ...patch }));
    }

    function addManagerCustomArea() {
        const text = managerCustomArea.trim();
        if (!text) return;
        setManagerForm((prev) => {
            const set = new Set(prev.serviceAreas || []);
            set.add(text);
            return { ...prev, serviceAreas: [...set] };
        });
        setManagerCustomArea("");
    }

    function handleCloseAuth() {
        if (asOverlay && typeof onDismiss === "function") {
            onDismiss();
            return;
        }
        if (isAdminLogin) {
            navigate("/", { replace: true });
            return;
        }
        const dest = returnToPath && returnToPath !== "/login" ? returnToPath : "/";
        navigate(dest, { replace: true });
    }

    function applyCapturedCoords(lat, lng, { silent = false, force = false } = {}) {
        if (!force && locationChosenByUserRef.current && silent) {
            return;
        }
        const latStr = Number(lat).toFixed(6);
        const lngStr = Number(lng).toFixed(6);
        const nearest = resolveNearestIndianCity(latStr, lngStr);
        const placeholder = `Current location (${latStr}, ${lngStr})`;
        setCreateValues((prev) => {
            // Explicit Use GPS always wins; silent auto-detect must not clobber a manual pick.
            const keepManualCity = !force && locationChosenByUserRef.current && prev.city;
            return {
                ...prev,
                latitude: latStr,
                longitude: lngStr,
                // Bind exact coords now; label comes from reverse geocode (not catalog snap).
                city: keepManualCity ? prev.city : placeholder,
            };
        });
        setCreateErrors((prev) => ({ ...prev, latitude: "", longitude: "", city: "" }));
        if (!silent) {
            locationChosenByUserRef.current = true;
            setGeoMessage("Detecting address…");
        }

        // Bind exact reverse-geocode label; catalog nearest is fallback only.
        reverseGeocodeAddress({ latitude: lat, longitude: lng }).then((geo) => {
            const exactLabel = geo
                ? formatExactLocationLabel({
                      locality: geo.locality,
                      city: geo.city,
                      state: geo.state,
                  })
                : "";
            const boundLabel =
                exactLabel ||
                (nearest?.withinRange ? nearest.label : "") ||
                (nearest?.label ? `Near ${nearest.label}` : "") ||
                placeholder;

            setCreateValues((prev) => {
                if (prev.latitude !== latStr || prev.longitude !== lngStr) return prev;
                if (!force && locationChosenByUserRef.current && silent && prev.city) {
                    const wasPlaceholder = String(prev.city).startsWith("Current location (");
                    if (!wasPlaceholder && prev.city !== boundLabel) return prev;
                }
                return { ...prev, city: boundLabel };
            });
            if (!silent) {
                if (exactLabel) {
                    setGeoMessage(`GPS detected: ${exactLabel}.`);
                } else if (nearest?.withinRange) {
                    setGeoMessage(
                        `GPS detected near ${nearest.label}. Confirm or pick your area if this looks wrong.`
                    );
                } else {
                    setGeoMessage("GPS captured. Pick your area from the list to label it.");
                }
            }
        });
    }

    function applyDefaultLocation({ silent = false } = {}) {
        if (locationChosenByUserRef.current && silent) return;
        setCreateValues((prev) => {
            // Prefer resolving the typed/selected city label to real coords over Bengaluru default.
            const fromCity = prev.city ? resolveIndianCity(prev.city) : null;
            if (fromCity) {
                return {
                    ...prev,
                    city: fromCity.label,
                    latitude: prev.latitude || String(fromCity.latitude),
                    longitude: prev.longitude || String(fromCity.longitude),
                };
            }
            // Do not invent Chennai/Bengaluru when GPS failed and no city was chosen.
            return prev;
        });
        setCreateErrors((prev) => ({ ...prev, latitude: "", longitude: "", city: "" }));
        if (!silent) {
            setGeoMessage("Could not access GPS. Type and select your city from the list.");
        }
    }

    function applyCaregiverSidebarUser(user) {
        ROLE_SIDEBAR_CONFIG.caregiver.user = {
            initials: `${user?.firstName?.charAt(0) || ""}${user?.lastName?.charAt(0) || ""}`,
            name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
            role: "Caregiver",
        };
        try {
            const mobile = normalizeIndianMobile(
                user?.mobile || user?.phone || user?.username || ""
            );
            if (mobile.length === 10) {
                window.localStorage.setItem(
                    "nema_caregiver_login_mobile_v1",
                    JSON.stringify({ userId: user?.id || null, mobile })
                );
            }
        } catch {
            /* ignore */
        }
    }

    function navigateAfterCaregiverAuth(data, { isNewRegistration = false, loginMessage = "" } = {}) {
        const onboarding = data?.caregiverOnboarding;
        const { path, showProfileHint } = resolveCaregiverPostAuthRoute(onboarding, {
            isNewRegistration,
        });
        const msg =
            loginMessage ||
            (isNewRegistration
                ? "Account created. Complete your profile to continue."
                : typeof data?.message === "string" && data.message.trim()
                  ? data.message.trim()
                  : "Login successful.");
        applyCaregiverSidebarUser({
            ...data?.user,
            mobile:
                data?.user?.mobile ||
                data?.user?.phone ||
                onboarding?.account?.mobile ||
                normalizeIndianMobile(loginValues.mobile) ||
                loginOtpMobile,
        });
        navigate(path, {
            replace: true,
            state: {
                loginMessage: msg,
                showProfileHint,
                profileSetupHint: showProfileHint ? getCaregiverProfileSetupHint(onboarding) : "",
                isNewCaregiver: isNewRegistration,
            },
        });
    }

    function switchTab(toCreate) {
        setShowCreate(toCreate);
        setShowLoginPassword(false);
        setLoginMessage("");
        setLoginError("");
        setCreateMessage("");
        setCreateError("");
        setGeoMessage("");
        setRoleError("");
        if (!toCreate) {
            resetSignupFlow();
            syncAuthOverlayParams({});
        } else {
            resetLoginFlow();
            if (!isHireFlow) {
                const urlRole = searchParams.get("role");
                if (urlRole === "caregiver" || urlRole === "manager" || urlRole === "customer") {
                    setSelectedRole(urlRole);
                } else if (!selectedRole) {
                    setSelectedRole("customer");
                }
                syncAuthOverlayParams({
                    tab: "create",
                    role: urlRole || selectedRole || "customer",
                });
            }
        }
    }

    function completeLoginResponse(data) {
        const session =
            data && typeof data === "object"
                ? data.token
                    ? data
                    : data.data && typeof data.data === "object"
                      ? data.data
                      : data
                : null;
        const jwt =
            session?.token || session?.accessToken || session?.jwt || data?.token || null;
        if (jwt) api.setToken(jwt);
        const msg =
            typeof data?.message === "string" && data.message.trim()
                ? data.message.trim()
                : "Login successful.";
        setLoginMessage(msg);

        const role = (data?.user?.role || data?.role || "").toUpperCase();
        const mobile = normalizeIndianMobile(loginValues.mobile) || loginOtpMobile;

        if (role === "CAREGIVER") {
            navigateAfterCaregiverAuth(data, { loginMessage: msg });
            return;
        }
        if (role === "CUSTOMER") {
            const apiFirst = String(data?.user?.firstName || "").trim();
            const apiLast = String(data?.user?.lastName || "").trim();
            const firstName = isUsableProfileName(apiFirst) ? apiFirst : "";
            const lastName = isUsableProfileName(apiFirst) ? apiLast : "";
            const fullName = [firstName, lastName].filter(Boolean).join(" ");
            const exactLocation = String(data?.user?.location || data?.user?.city || "").trim();
            ROLE_SIDEBAR_CONFIG.customer.user = {
                initials: `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`,
                name: fullName || mobile,
                role: "Customer account",
            };
            api.setStoredCustomerProfile({
                firstName,
                lastName,
                fullName,
                mobile: data?.user?.mobile || data?.user?.phone || mobile,
                email: String(data?.user?.email || "").trim(),
                city: exactLocation,
                location: exactLocation,
                latitude: data?.user?.latitude || "",
                longitude: data?.user?.longitude || "",
                emergencyContact: data?.user?.emergencyContact || "",
                isInsured: Boolean(data?.user?.isInsured ?? data?.user?.insured),
                insuranceProvider:
                    data?.user?.insuranceProvider || data?.user?.insurance_provider || "",
                policyNumber:
                    data?.user?.policyNumber || data?.user?.insurance_policy_number || "",
                userId: data?.user?.id || "U001",
                memberSince: data?.user?.createdAt || "",
                status: "Active",
            });
            const customerMsg =
                searchParams.get("role") === "caregiver"
                    ? "Signed in to your customer account. To join as a caregiver, use Create Account with a different mobile number."
                    : msg;
            if (hasPendingCareHubBookingDraft(data?.user?.id)) {
                navigateResumeCareHubBooking(navigate, { loginMessage: customerMsg });
                return;
            }
            const dest = buildPostCustomerAuthNavigation(searchParams, { loginMessage: customerMsg });
            navigate(dest.path, { replace: dest.replace !== false, state: dest.state });
            return;
        }
        if (role === "CARE_MANAGER") {
            ROLE_SIDEBAR_CONFIG.careManager.user = {
                initials: `${data?.user?.firstName?.charAt(0) || ""}${data?.user?.lastName?.charAt(0) || ""}`,
                name: `${data?.user?.firstName || ""} ${data?.user?.lastName || ""}`.trim(),
                role: "Care Manager",
            };
            navigate("/care-manager", { replace: true, state: { loginMessage: msg } });
            return;
        }
        if (role === "ADMIN" || role === "OPS") {
            const adminReturn = searchParams.get("returnTo");
            navigate(
                adminReturn && adminReturn.startsWith("/admin") ? adminReturn : "/admin",
                { replace: true, state: { loginMessage: msg } }
            );
            return;
        }

        navigate("/dashboard", { replace: true, state: { loginMessage: msg } });
    }

    function handleLoginChange(e) {
        const { name, value } = e.target;
        const nextValue = name === "mobile" ? sanitizeLoginIdInput(value) : value;
        setLoginValues((prev) => ({ ...prev, [name]: nextValue }));
        setLoginErrors((prev) => ({ ...prev, [name]: "" }));
        setLoginError("");
        setLoginMessage("");
    }

    function handleLoginBlur(e) {
        const { name, value } = e.target;
        if (isAdminLogin) {
            setLoginErrors((prev) => ({
                ...prev,
                [name]:
                    name === "mobile"
                        ? validateLoginMobile(value)
                        : validatePassword(value),
            }));
            return;
        }
        setLoginErrors((prev) => ({
            ...prev,
            [name]: name === "mobile" ? validateLoginMobile(value) : "",
        }));
    }

    function validateLoginForm() {
        const mobile = normalizeIndianMobile(loginValues.mobile);
        const username = String(loginValues.mobile || "").trim();
        const requiresPasswordLogin = isAdminLogin;

        if (requiresPasswordLogin) {
            const next = {
                mobile: validateLoginMobile(loginValues.mobile),
                password: validatePassword(loginValues.password),
            };
            setLoginErrors(next);
            return !next.mobile && !next.password;
        }

        const next = {
            mobile: validateLoginMobile(loginValues.mobile),
            password: "",
        };
        if (mobile) {
            next.password = "";
        } else {
            next.mobile = "Enter your 10-digit mobile number.";
        }
        setLoginErrors(next);
        return !next.mobile && !next.password;
    }

    async function handleLoginSubmit(e) {
        e.preventDefault();
        setLoginError("");
        setLoginMessage("");
        setRoleError("");

        if (loginStep === 1) {
            if (!validateLoginForm()) return;
            const mobile = normalizeIndianMobile(loginValues.mobile);
            const username = mobile || loginValues.mobile.trim();
            const userType = resolveLoginUserType({ isAdminLogin, mobile, selectedRole });
            const requiresPasswordLogin = isAdminLogin;
            setLoginLoading(true);
            try {
                if (requiresPasswordLogin || !mobile) {
                    const data = await api.adminLogin({
                        username,
                        password: loginValues.password,
                        userType,
                    });
                    completeLoginResponse(data);
                } else {
                    const res = await api.requestLoginOtp({
                        username,
                        userType,
                    });
                    const otpMobile = res?.mobile || mobile || "";
                    setLoginOtpMobile(otpMobile);
                    setOtpMessage(formatOtpSentMessage());
                    if (IS_DEV) setOtpCode(STUB_OTP);
                    setLoginStep(2);
                }
            } catch (err) {
                const msg = formatUserFacingError(err, "Login failed.");
                if (/choose|role does not match/i.test(msg)) setRoleError(msg);
                applyAuthError(err, setLoginError, "Login failed.");
            } finally {
                setLoginLoading(false);
            }
            return;
        }

        await verifyAndLoginWithOtp();
    }

    async function resendLoginOtp() {
        const mobile = normalizeIndianMobile(loginValues.mobile);
        const username = mobile || loginValues.mobile.trim();
        if (!mobile) {
            setLoginError("Username login does not use OTP. Go back and sign in with password.");
            return false;
        }
        setOtpSending(true);
        setLoginError("");
        setOtpMessage("");
        try {
            const res = await api.requestLoginOtp({
                username,
                userType: resolveLoginUserType({ isAdminLogin, mobile, selectedRole }),
            });
            const otpMobile = res?.mobile || loginOtpMobile || mobile || "";
            setLoginOtpMobile(otpMobile);
            setOtpMessage(formatOtpSentMessage());
            if (IS_DEV) setOtpCode(STUB_OTP);
            return true;
        } catch (err) {
            applyAuthError(err, setLoginError, "Unable to resend OTP.");
            return false;
        } finally {
            setOtpSending(false);
        }
    }

    async function verifyAndLoginWithOtp() {
        const mobile = loginOtpMobile || normalizeIndianMobile(loginValues.mobile);
        const code = String(otpCode || "").trim();
        if (!mobile || !isValidOtpInput(code)) {
            setLoginError(`Enter the ${OTP_LENGTH}-digit verification code.`);
            return;
        }

        setLoginLoading(true);
        setLoginError("");
        try {
            const verifyRes = await api.verifyPhoneOtp({ mobile, purpose: "LOGIN", otp: code });
            const token = verifyRes?.phoneOtpVerificationToken || verifyRes?.verificationToken || "";
            if (!token) {
                setLoginError("OTP verified but token missing. Try again.");
                return;
            }
            const data = await api.loginWithPhoneOtp({
                mobile,
                phoneOtpVerificationToken: token,
                userType: resolveLoginUserType({ isAdminLogin, mobile, selectedRole }),
            });
            completeLoginResponse(data);
        } catch (err) {
            const msg = formatUserFacingError(err, "Invalid or expired OTP.");
            if (/choose|role does not match/i.test(msg)) setRoleError(msg);
            applyAuthError(err, setLoginError, "Invalid or expired OTP.");
        } finally {
            setLoginLoading(false);
        }
    }

    function handleCreateChange(e) {
        const { name, value, type, checked } = e.target;
        const nextValue = name === "mobile" ? sanitizeMobileDigits(value) : value;
        if (name === "fullName") {
            const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
            setCreateValues((prev) => ({
                ...prev,
                fullName: value,
                firstName: parts[0] || "",
                lastName: parts.slice(1).join(" "),
            }));
            setCreateErrors((prev) => ({ ...prev, fullName: "", firstName: "" }));
            setCreateMessage("");
            setCreateError("");
            return;
        }
        setCreateValues((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : nextValue,
        }));
        setCreateErrors((prev) => ({ ...prev, [name]: "" }));
        setCreateMessage("");
        setCreateError("");
    }

    function handleCustomerCitySelect(city) {
        if (!city) return;
        locationChosenByUserRef.current = true;
        setCreateValues((prev) => ({
            ...prev,
            city: city.label,
            latitude: String(city.latitude),
            longitude: String(city.longitude),
        }));
        setCreateErrors((prev) => ({ ...prev, city: "", latitude: "", longitude: "" }));
        setGeoMessage(`Location set to ${city.label}.`);
    }

    function handleCustomerCityChange(label) {
        const resolved = resolveIndianCity(label);
        if (resolved) {
            handleCustomerCitySelect(resolved);
            return;
        }
        // Free-text / incomplete — clear stale coords so we don't keep a previous city's pin.
        locationChosenByUserRef.current = Boolean(String(label || "").trim());
        setCreateValues((prev) => ({
            ...prev,
            city: label,
            latitude: "",
            longitude: "",
        }));
        setCreateErrors((prev) => ({ ...prev, city: "" }));
        setGeoMessage("");
    }

    function handleCustomerCityCommit(label) {
        const resolved = resolveIndianCity(label);
        if (resolved) {
            handleCustomerCitySelect(resolved);
            return;
        }
        // Exact GPS / reverse-geocode labels are valid when coordinates are already bound.
        const trimmed = String(label || "").trim();
        if (
            trimmed &&
            createValues.latitude &&
            createValues.longitude &&
            String(createValues.city || "").trim() === trimmed
        ) {
            setCreateErrors((prev) => ({ ...prev, city: "", latitude: "", longitude: "" }));
            return;
        }
        if (trimmed) {
            setCreateErrors((prev) => ({
                ...prev,
                city: "Select a city from the list, or tap Use GPS.",
            }));
        }
    }

    function detectLocation({ silent = false } = {}) {
        if (!silent) {
            setGeoMessage("");
            setCreateError("");
            // Explicit Use GPS must override a previous wrong city (e.g. Chennai).
            locationChosenByUserRef.current = false;
        }
        if (silent && locationChosenByUserRef.current) return;
        if (!("geolocation" in navigator)) {
            applyDefaultLocation({ silent });
            return;
        }
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                applyCapturedCoords(position.coords.latitude, position.coords.longitude, {
                    silent,
                    force: !silent,
                });
                setGeoLoading(false);
            },
            () => {
                applyDefaultLocation({ silent });
                setGeoLoading(false);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
    }

    async function sendSignupOtp(purpose) {
        const mobile = normalizeIndianMobile(createValues.mobile);
        if (!mobile) {
            setCreateError("Enter a valid 10-digit mobile number.");
            return false;
        }
        setOtpSending(true);
        setCreateError("");
        setOtpMessage("");
        try {
            await api.sendPhoneOtp({ mobile, purpose });
            setOtpMessage(formatOtpSentMessage());
            if (IS_DEV) setOtpCode(STUB_OTP);
            setSignupOtpSent(true);
            setOtpSecondsLeft(OTP_COUNTDOWN_SECONDS);
            return true;
        } catch (err) {
            const msg = formatUserFacingError(err, "Unable to send OTP.");
            if (/already registered/i.test(msg)) {
                setCreateErrors((prev) => ({
                    ...prev,
                    mobile: "This mobile number is already registered. Please sign in instead.",
                }));
            }
            applyAuthError(err, setCreateError, "Unable to send OTP.");
            return false;
        } finally {
            setOtpSending(false);
        }
    }

    async function verifySignupOtp(purpose) {
        const mobile = normalizeIndianMobile(createValues.mobile);
        const code = String(otpCode || "").trim();
        if (!mobile || !isValidOtpInput(code)) {
            setCreateError(`Enter the ${OTP_LENGTH}-digit verification code.`);
            return null;
        }
        setCreateLoading(true);
        setCreateError("");
        try {
            const res = await api.verifyPhoneOtp({ mobile, purpose, otp: code });
            const token = res?.phoneOtpVerificationToken || res?.verificationToken || "";
            if (!token) {
                setCreateError("OTP verified but token missing. Try again.");
                return null;
            }
            setPhoneOtpVerificationToken(token);
            return token;
        } catch (err) {
            applyAuthError(err, setCreateError, "Invalid or expired OTP.");
            return null;
        } finally {
            setCreateLoading(false);
        }
    }

    function applyLoginSession(data, role, options = {}) {
        const msg =
            typeof data?.message === "string" && data.message.trim()
                ? data.message.trim()
                : "Account created successfully.";
        const authRole = (data?.user?.role || data?.role || roleToUserType(selectedRole) || "").toUpperCase();
        const isNewRegistration = Boolean(options.isNewRegistration);

        if (authRole === "CAREGIVER") {
            navigateAfterCaregiverAuth(data, { isNewRegistration, loginMessage: msg });
            return;
        }
        if (authRole === "CUSTOMER") {
            const signupFullName =
                String(createValues.fullName || "").trim() ||
                `${createValues.firstName || ""} ${createValues.lastName || ""}`.trim();
            const signupParts = signupFullName.split(/\s+/).filter(Boolean);
            const localFirst = signupParts[0] || String(createValues.firstName || "").trim();
            const localLast =
                signupParts.slice(1).join(" ") || String(createValues.lastName || "").trim();
            const apiFirst = String(data?.user?.firstName || "").trim();
            const apiLast = String(data?.user?.lastName || "").trim();
            const firstName = isUsableProfileName(apiFirst)
                ? apiFirst
                : isUsableProfileName(localFirst)
                  ? localFirst
                  : "";
            const lastName = isUsableProfileName(apiFirst) ? apiLast : localLast;
            const fullName =
                [firstName, lastName].filter(Boolean).join(" ") ||
                (isUsableProfileName(signupFullName) ? signupFullName : "");
            // Prefer exact signup location label over any catalog snap returned by the API.
            const exactLocation =
                String(createValues.city || "").trim() ||
                String(data?.user?.location || data?.user?.city || "").trim();
            ROLE_SIDEBAR_CONFIG.customer.user = {
                initials: `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`,
                name: fullName,
                role: "Customer account",
            };
            api.setStoredCustomerProfile({
                firstName,
                lastName,
                fullName,
                mobile: data?.user?.mobile || data?.user?.phone || createValues.mobile,
                email: String(createValues.email || data?.user?.email || "").trim(),
                gender: createValues.gender || data?.user?.gender || "",
                city: exactLocation,
                location: exactLocation,
                latitude: createValues.latitude || data?.user?.latitude || "",
                longitude: createValues.longitude || data?.user?.longitude || "",
                emergencyContact: data?.user?.emergencyContact || "",
                isInsured: Boolean(data?.user?.isInsured ?? data?.user?.insured),
                insuranceProvider:
                    data?.user?.insuranceProvider || data?.user?.insurance_provider || "",
                policyNumber:
                    data?.user?.policyNumber || data?.user?.insurance_policy_number || "",
                userId: data?.user?.id || "U001",
                memberSince: data?.user?.createdAt || "",
                status: "Active",
            });

            if (isNewRegistration) {
                if (hasPendingCareHubBookingDraft(data?.user?.id)) {
                    navigateResumeCareHubBooking(navigate, {
                        loginMessage: "Account created. Continue your booking where you left off.",
                    });
                    return;
                }

                const resumeCareHubBooking =
                    resolveAuthAction(searchParams) === AUTH_ACTIONS.BOOK &&
                    (searchParams.get("caregiverId") || searchParams.get("hire"));
                if (resumeCareHubBooking) {
                    const dest = buildPostCustomerAuthNavigation(searchParams, {
                        loginMessage: "Account created. Continue your booking.",
                    });
                    navigate(dest.path, {
                        replace: dest.replace !== false,
                        state: { ...dest.state, bookingResume: true },
                    });
                    return;
                }

                setHireRegistrationMeta({
                    city: createValues.city || data?.user?.city || "",
                });
                setCustomerHireOnboarding(true);
                setShowCreate(true);
                return;
            }

            if (hasPendingCareHubBookingDraft(data?.user?.id)) {
                navigateResumeCareHubBooking(navigate, { loginMessage: msg });
                return;
            }

            const dest = buildPostCustomerAuthNavigation(searchParams, { loginMessage: msg });
            navigate(dest.path, { replace: dest.replace !== false, state: dest.state });
            return;
        }
        if (authRole === "CARE_MANAGER") {
            ROLE_SIDEBAR_CONFIG.careManager.user = {
                initials: `${data?.user?.firstName?.charAt(0) || ""}${data?.user?.lastName?.charAt(0) || ""}`,
                name: `${data?.user?.firstName || ""} ${data?.user?.lastName || ""}`.trim(),
                role: "Care Manager",
            };
            navigate(isNewRegistration ? "/care-manager/onboarding" : "/care-manager", {
                replace: true,
                state: {
                    loginMessage: isNewRegistration
                        ? "Application submitted — complete KYC documents and track compliance in your onboarding portal."
                        : msg,
                },
            });
            return;
        }
        navigate("/admin", { replace: true, state: { loginMessage: msg } });
    }


    /**
     * Real ad-click attribution (UTM/fbclid/gclid) always wins over a manual
     * "how did you hear about us?" dropdown pick when both exist, so CPA/source
     * reporting reflects actual ad spend instead of a guess. The manual pick is
     * still recorded in leadSourceMetadata.userSelectedLeadSource for visibility.
     */
    function resolveLeadSourceForSubmit() {
        const firstTouch = getFirstTouchAttribution();
        const mapped = firstTouch ? mapAttributionToLeadSource(firstTouch) : null;
        const finalLeadSource = mapped?.leadSourceId || createValues.leadSource || undefined;
        const finalLeadSourceChannel = mapped?.leadSourceChannel || undefined;
        const leadSourceMetadata =
            firstTouch || createValues.leadSource
                ? { ...(firstTouch || {}), userSelectedLeadSource: createValues.leadSource || null }
                : undefined;
        return { finalLeadSource, finalLeadSourceChannel, leadSourceMetadata };
    }

    async function finalizeAccountCreation(extra = {}) {
        const mobile = normalizeIndianMobile(createValues.mobile);
        let cityLabel = (createValues.city || "").trim();
        const { generatedPassword: _unusedPwd, ...restExtra } = extra;
        let lat = createValues.latitude;
        let lng = createValues.longitude;

        // Ensure city label and coordinates stay in sync before create.
        const resolvedCity = resolveIndianCity(cityLabel);
        if (resolvedCity) {
            cityLabel = resolvedCity.label;
            if (!lat || !lng) {
                lat = String(resolvedCity.latitude);
                lng = String(resolvedCity.longitude);
            }
        }

        if ((!lat || !lng) && !isHireFlow) {
            setCreateErrors((prev) => ({
                ...prev,
                city: "Select a city from the list, or tap Use GPS.",
                latitude: !lat ? "Valid latitude is required." : prev.latitude,
                longitude: !lng ? "Valid longitude is required." : prev.longitude,
            }));
            setCreateError("Your location is incomplete. Select a city or use GPS.");
            return;
        }

        lat = lat || (!isHireFlow ? "" : DEFAULT_COORDS.latitude);
        lng = lng || (!isHireFlow ? "" : DEFAULT_COORDS.longitude);
        const signupFullName =
            String(createValues.fullName || "").trim() ||
            `${createValues.firstName || ""} ${createValues.lastName || ""}`.trim();
        const nameParts = signupFullName.split(/\s+/).filter(Boolean);
        const signupFirstName = nameParts[0] || "";
        const signupLastName = nameParts.slice(1).join(" ");
        if (!isUsableProfileName(signupFullName)) {
            setCreateErrors((prev) => ({
                ...prev,
                fullName: "Enter your full name (not your mobile number).",
                firstName: "Enter your full name (not your mobile number).",
            }));
            setCreateError("Enter your full name to create the account.");
            return;
        }
        const resolvedLeadSource = resolveLeadSourceForSubmit();
        const payload = {
            userType: roleToUserType(selectedRole) || "ADMIN",
            username: mobile,
            mobile,
            firstName: signupFirstName,
            lastName: signupLastName,
            fullName: signupFullName,
            email: String(createValues.email || "").trim() || undefined,
            city: cityLabel,
            location: cityLabel,
            latitude: Number(lat),
            longitude: Number(lng),
          password: createValues.password || undefined,
            phoneOtpVerificationToken: phoneOtpVerificationToken || restExtra.phoneOtpVerificationToken,
            gender: normalizeGender(createValues.gender) || undefined,
            serviceRole: createValues.serviceRole?.trim() || "attendant",
            leadSource: resolvedLeadSource.finalLeadSource,
            leadSourceChannel: resolvedLeadSource.finalLeadSourceChannel,
            leadSourceMetadata: resolvedLeadSource.leadSourceMetadata,
            ...restExtra,
        };
        setCreateLoading(true);
        try {
            // Care manager signup no longer flows through here — it registers via
            // finalizeCareManagerPhoneSignup (POST /care-manager/onboarding/register).
            const createData = await api.createUser(payload);
            if (createData?.token) {
                if (createData.token) api.setToken(createData.token);
                trackPixelEvent("CompleteRegistration", { role: selectedRole, leadSource: resolvedLeadSource.finalLeadSource });
                trackGa4Event("sign_up", { role: selectedRole, lead_source: resolvedLeadSource.finalLeadSource });
                clearFirstTouchAttribution();
                applyLoginSession(createData, undefined, { isNewRegistration: true });
                setCreateValues(INIT_CREATE);
                setCreateErrors(INIT_CREATE_ERRORS);
                resetSignupFlow();
                setGeoMessage("");
                return;
            }
            // Fallback: start passwordless OTP sign-in after account create.
            const msg = "Account created. Enter the OTP sent to your mobile to sign in.";
            setCreateMessage(msg);
            setLoginMessage(msg);
            setLoginValues({ mobile, password: "" });
            setCreateValues(INIT_CREATE);
            setCreateErrors(INIT_CREATE_ERRORS);
            resetSignupFlow();
            setGeoMessage("");
            switchTab(false);
            try {
                const res = await api.requestLoginOtp({ username: mobile });
                setLoginOtpMobile(res?.mobile || mobile);
                setOtpMessage(formatOtpSentMessage());
                if (IS_DEV) setOtpCode(STUB_OTP);
                setLoginStep(2);
            } catch {
                /* user can request OTP from sign-in */
            }
        } catch (err) {
            const msg = formatUserFacingError(err, "Unable to create account.");
            if (/already registered/i.test(msg)) {
                const isCaregiverSignup = selectedRole === "caregiver";
                const mobileMsg = isCaregiverSignup
                    ? "This mobile is already registered as a customer account. Sign in as customer, or use a different mobile number to register as a caregiver."
                    : "This mobile number is already registered. Please sign in instead.";
                setCreateErrors((prev) => ({ ...prev, mobile: mobileMsg }));
                setCustomerSignupStep(1);
                setCaregiverSignupStep(1);
                setManagerSignupStep(1);
                setOtpCode("");
                setOtpMessage("");
                setCreateError(mobileMsg);
                showToast.info(mobileMsg);
            } else {
                applyAuthError(err, setCreateError, "Unable to create account.");
            }
        } finally {
            setCreateLoading(false);
        }
    }

    async function finalizeCareManagerPhoneSignup(phoneOtpVerificationToken, intake = managerForm) {
        const mobile = normalizeIndianMobile(createValues.mobile);
        if (!mobile) {
            setCreateErrors((prev) => ({ ...prev, mobile: "Enter a valid 10-digit mobile number." }));
            setCreateError("Enter a valid 10-digit mobile number.");
            return;
        }
        const firstName = String(createValues.firstName || "").trim();
        const lastName = String(createValues.lastName || "").trim();
        setCreateLoading(true);
        const resolvedLeadSource = resolveLeadSourceForSubmit();
        try {
            const createData = await api.registerCareManagerOnboarding({
                firstName,
                lastName,
                mobile,
                email: String(createValues.email || "").trim() || undefined,
                phoneOtpVerificationToken,
                termsVersion: CARE_MANAGER_TERMS_VERSION,
                leadSource: resolvedLeadSource.finalLeadSource,
                leadSourceChannel: resolvedLeadSource.finalLeadSourceChannel,
                leadSourceMetadata: resolvedLeadSource.leadSourceMetadata,
                city: intake.city,
                state: intake.state,
                locality: intake.serviceAreas?.[0] || "",
                pinCode: normalizeCmPin(intake.pinCode),
                serviceAreas: intake.serviceAreas || [],
                servicesOffered: intake.servicesOffered || [],
                managerType: intake.managerType,
                qualificationSummary: String(intake.qualificationSummary || "").trim(),
                experienceYears: Number(intake.experienceYears),
                currentEmploymentStatus: String(intake.employmentStatus || "").trim(),
            });

            const successMessage = "Account created successfully.";
            if (createData?.token) {
                api.setToken(createData.token);
                trackPixelEvent("CompleteRegistration", { role: "care_manager", leadSource: resolvedLeadSource.finalLeadSource });
                trackGa4Event("sign_up", { role: "care_manager", lead_source: resolvedLeadSource.finalLeadSource });
                clearFirstTouchAttribution();
            }

            applyLoginSession(
                { ...createData, message: successMessage },
                "CARE_MANAGER",
                { isNewRegistration: true }
            );
            setCreateValues(INIT_CREATE);
            setCreateErrors(INIT_CREATE_ERRORS);
            resetSignupFlow();
            setGeoMessage("");
        } catch (err) {
            const msg = formatUserFacingError(err, "Unable to create care manager account.");
            if (/already registered/i.test(msg)) {
                const mobileMsg = "This mobile number is already registered. Please sign in instead.";
                setCreateErrors((prev) => ({ ...prev, mobile: mobileMsg }));
                setManagerSignupStep(1);
                setOtpCode("");
                setOtpMessage("");
                setCreateError(mobileMsg);
                showToast.info(mobileMsg);
            } else {
                applyAuthError(err, setCreateError, "Unable to create care manager account.");
            }
        } finally {
            setCreateLoading(false);
        }
    }

    async function finalizeCaregiverPhoneSignup(phoneOtpVerificationToken) {
        const mobile = normalizeIndianMobile(createValues.mobile);
        if (!mobile) {
            setCreateErrors((prev) => ({ ...prev, mobile: "Enter a valid 10-digit mobile number." }));
            setCreateError("Enter a valid 10-digit mobile number.");
            return;
        }
        setCreateLoading(true);
        const resolvedLeadSource = resolveLeadSourceForSubmit();
        try {
            const createData = await api.caregiverOnboardingRegister({
                mobile,
                phoneOtpVerificationToken,
                firstName: "",
                lastName: "",
                leadSource: resolvedLeadSource.finalLeadSource,
                leadSourceChannel: resolvedLeadSource.finalLeadSourceChannel,
                leadSourceMetadata: resolvedLeadSource.leadSourceMetadata,
            });
            if (createData?.token) {
                api.setToken(createData.token);
                trackPixelEvent("CompleteRegistration", { role: "caregiver", leadSource: resolvedLeadSource.finalLeadSource });
                trackGa4Event("sign_up", { role: "caregiver", lead_source: resolvedLeadSource.finalLeadSource });
                clearFirstTouchAttribution();
                try {
                    window.localStorage.setItem(
                        "nema_caregiver_login_mobile_v1",
                        JSON.stringify({
                            userId: createData?.user?.id || api.getCurrentUserIdFromToken?.(),
                            mobile,
                        })
                    );
                } catch {
                    /* ignore */
                }
                applyLoginSession(createData, "CAREGIVER", { isNewRegistration: true });
                setCreateValues(INIT_CREATE);
                setCreateErrors(INIT_CREATE_ERRORS);
                resetSignupFlow();
                setGeoMessage("");
                return;
            }
            setCreateError("Account created, but sign-in failed. Please sign in with OTP.");
            setLoginValues({ mobile, password: "" });
            switchTab(false);
        } catch (err) {
            const msg = formatUserFacingError(err, "Unable to create caregiver account.");
            if (/already registered/i.test(msg)) {
                const mobileMsg =
                    "This mobile is already registered. Sign in with OTP to continue onboarding.";
                setCreateErrors((prev) => ({ ...prev, mobile: mobileMsg }));
                setCaregiverSignupStep(1);
                setOtpCode("");
                setOtpMessage("");
                setCreateError(mobileMsg);
                showToast.info(mobileMsg);
            } else {
                applyAuthError(err, setCreateError, "Unable to create caregiver account.");
            }
        } finally {
            setCreateLoading(false);
        }
    }

    async function handleCreateSubmit(e) {
        e.preventDefault();
        setCreateMessage("");
        setCreateError("");
        setRoleError("");
        if (!selectedRole) {
            setRoleError("Please select a role to create account.");
            return;
        }

        if (selectedRole === "customer") {
            setCreateValues((prev) => ({ ...prev, agreeTerms: true }));
            if (!signupOtpSent) {
                setCreateError("Please enter your mobile number to receive an OTP.");
                return;
            }
            let draftValues = { ...createValues };
            if ((!draftValues.latitude || !draftValues.longitude) && draftValues.city?.trim()) {
                const resolved = resolveIndianCity(draftValues.city);
                if (resolved) {
                    draftValues = {
                        ...draftValues,
                        latitude: String(resolved.latitude),
                        longitude: String(resolved.longitude),
                    };
                }
            }
            if (isHireFlow && (!draftValues.latitude || !draftValues.longitude)) {
                draftValues = { ...draftValues, ...DEFAULT_COORDS };
            }
            const { errors, isValid } = validateCreateForm(draftValues, {
                requireTerms: false,
                requireGender: false,
                requirePassword: false,
                requireRelationship: false,
                requireCity: true,
            });
            setCreateErrors(errors);
            if (!isValid) return;
            if (draftValues !== createValues) {
                setCreateValues(draftValues);
            }
            const token = await verifySignupOtp("CUSTOMER_SIGNUP");
            if (!token) return;
            const extra = { phoneOtpVerificationToken: token };
            await finalizeAccountCreation(extra);
            return;
        }

        if (selectedRole === "caregiver") {
            if (caregiverSignupStep === 1) {
                const { errors, isValid } = validateCreateForm(createValues, {
                    requireName: false,
                    requireGender: false,
                    requireServiceRole: false,
                    requirePassword: false,
                    requireLocation: false,
                    requireTerms: true,
                });
                setCreateErrors(errors);
                if (!isValid) return;
                const sent = await sendSignupOtp("CAREGIVER_SIGNUP");
                if (sent) setCaregiverSignupStep(2);
                return;
            }
            const token = await verifySignupOtp("CAREGIVER_SIGNUP");
            if (token) await finalizeCaregiverPhoneSignup(token);
            return;
        }

        if (selectedRole === "manager") {
            if (managerSignupStep < MANAGER_SIGNUP_TOTAL_STEPS) {
                const msg = validateManagerSignupStep(managerSignupStep, createValues, managerForm);
                if (msg) {
                    setCreateError(msg);
                    return;
                }
                setCreateError("");
                setManagerSignupStep((s) => s + 1);
                return;
            }
            const termsMsg = validateManagerSignupStep(5, createValues, managerForm);
            if (termsMsg) {
                setManagerTermsError(termsMsg);
                return;
            }
            setManagerTermsError("");
            if (!signupOtpSent) {
                const sent = await sendSignupOtp("CARE_MANAGER_SIGNUP");
                if (sent) setCreateMessage("Enter the verification code below to submit your application.");
                return;
            }
            const token = await verifySignupOtp("CARE_MANAGER_SIGNUP");
            if (token) await finalizeCareManagerPhoneSignup(token, managerForm);
            return;
        }

        const { errors, isValid, mobile } = validateCreateForm(createValues, {});
        setCreateErrors(errors);
        if (!isValid) return;
        await finalizeAccountCreation({ username: mobile, mobile });
    }

    function renderAuthTrustPanel() {
        const isRegister = showCreate && !isAdminLogin;
        return (
            <aside className="auth-trust">
                <div className="auth-trust__top">
                    <div className="auth-trust__shield-wrap" aria-hidden="true">
                        <div className="auth-trust__shield">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                <path d="M9 12l2 2 4-4" strokeWidth="2.5" />
                            </svg>
                        </div>
                        <span className="auth-trust__shield-badge">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                    </div>
                    <h1>
                        {isAdminLogin ? (
                            <>
                                Admin <em>Console</em>
                            </>
                        ) : isRegister ? (
                            <>
                                Register to <em>Continue</em>
                            </>
                        ) : (
                            <>
                                Sign in to <em>Continue</em>
                            </>
                        )}
                    </h1>
                    <p className="auth-trust__lead">
                        {isAdminLogin
                            ? "Authorized operations access only. Customer and caregiver sign-in is available from the public website."
                            : isRegister
                              ? "Register to continue with caregiver hiring and receive service updates."
                              : "Sign in to book caregivers, manage visits, and receive care updates."}
                    </p>
                </div>

                <div className="auth-trust__art" aria-hidden="true">
                    <svg className="auth-trust__art-svg" viewBox="0 0 240 210" fill="none">
                        <ellipse
                            cx="42"
                            cy="158"
                            rx="46"
                            ry="26"
                            fill="#D8E8F8"
                            opacity="0.9"
                            transform="rotate(-16 42 158)"
                        />
                        <ellipse
                            cx="198"
                            cy="92"
                            rx="48"
                            ry="28"
                            fill="#CDDFF5"
                            opacity="0.75"
                            transform="rotate(18 198 92)"
                        />
                        <ellipse cx="152" cy="176" rx="34" ry="18" fill="#E6EFFA" opacity="0.95" />
                        <rect
                            x="84"
                            y="26"
                            width="72"
                            height="132"
                            rx="13"
                            fill="#fff"
                            stroke="#C5D9EF"
                            strokeWidth="1.5"
                        />
                        <rect x="91" y="40" width="58" height="100" rx="5" fill="#F7FAFE" />
                        <circle cx="120" cy="72" r="13" fill="#C5D9F2" />
                        <path
                            d="M103 98c0-9 8-15 17-15s17 6 17 15"
                            fill="#C5D9F2"
                        />
                        <path
                            d="M120 124l-16-8V94c0-6 7-10 16-10s16 4 16 10v22l-16 8z"
                            fill="#1A4DB2"
                        />
                        <path
                            d="M113 102l5 5 10-10"
                            stroke="#fff"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>

                <div className="auth-trust-card">
                    <h3>Your Data is Safe with Us</h3>
                    <ul className="auth-trust-list">
                        <li className="auth-trust-item">
                            <span className="auth-trust-item__icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                                    <rect x="3" y="11" width="18" height="11" rx="2" />
                                    <path d="M7 11V7a5 5 0 0110 0v4" />
                                </svg>
                            </span>
                            <div className="auth-trust-item__copy">
                                <strong>Secure &amp; Private</strong>
                                <span>Your information is encrypted and secure.</span>
                            </div>
                        </li>
                        <li className="auth-trust-item">
                            <span className="auth-trust-item__icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    <path d="M9 12l2 2 4-4" strokeWidth="2" />
                                </svg>
                            </span>
                            <div className="auth-trust-item__copy">
                                <strong>Trusted Platform</strong>
                                <span>Background verified caregivers.</span>
                            </div>
                        </li>
                        <li className="auth-trust-item">
                            <span className="auth-trust-item__icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                                    <path d="M3 18v-6a9 9 0 0118 0v6" />
                                    <path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z" />
                                </svg>
                            </span>
                            <div className="auth-trust-item__copy">
                                <strong>24/7 Support</strong>
                                <span>We&apos;re here to help you.</span>
                            </div>
                        </li>
                    </ul>
                </div>
            </aside>
        );
    }

    async function handleSendCustomerOtp({ hireMode = false } = {}) {
        setCreateError("");
        if (hireMode) {
            const mobile = normalizeIndianMobile(createValues.mobile);
            if (!mobile) {
                setCreateErrors((prev) => ({
                    ...prev,
                    mobile: "Enter a valid 10-digit mobile number.",
                }));
                return;
            }
            await sendSignupOtp("CUSTOMER_SIGNUP");
            return;
        }
        const mobile = normalizeIndianMobile(createValues.mobile);
        if (!mobile) {
            setCreateErrors((prev) => ({
                ...prev,
                mobile: "Enter a valid 10-digit mobile number.",
            }));
            return;
        }
        await sendSignupOtp("CUSTOMER_SIGNUP");
    }

    function renderCustomerRegistrationPopupForm() {
        const mobileId = isHireFlow ? "hire-mobile" : "create-mobile";
        const otpId = isHireFlow ? "hire-otp" : "signup-otp";

        return (
            <form
                onSubmit={handleCreateSubmit}
                className="login-form create-form auth-register-form auth-register-popup"
                noValidate
            >
                <div className="auth-step">
                    <div className="auth-step__head">
                        <span className="auth-step__num">1</span>
                        <h3 className="auth-step__title">Your Details</h3>
                    </div>
                    <div className="auth-field">
                        <label className="auth-field__label" htmlFor="create-full-name">
                            Full Name
                        </label>
                        <div className="auth-input-wrap">
                            <span className="auth-input-wrap__icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                            </span>
                            <input
                                id="create-full-name"
                                name="fullName"
                                type="text"
                                autoComplete="name"
                                value={createValues.fullName}
                                onChange={handleCreateChange}
                                className={`login-form__input${createErrors.fullName || createErrors.firstName ? " login-form__input--error" : ""}`}
                                placeholder="Enter your full name"
                            />
                        </div>
                        {(createErrors.fullName || createErrors.firstName) && (
                            <p className="login-form__field-error">
                                {createErrors.fullName || createErrors.firstName}
                            </p>
                        )}
                    </div>
                    <div className="auth-field">
                        <label className="auth-field__label" htmlFor="create-email">
                            Email Address <span className="auth-field__optional">(optional)</span>
                        </label>
                        <div className="auth-input-wrap">
                            <span className="auth-input-wrap__icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 4h16v16H4z" />
                                    <path d="M22 6l-10 7L2 6" />
                                </svg>
                            </span>
                            <input
                                id="create-email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                value={createValues.email}
                                onChange={handleCreateChange}
                                className={`login-form__input${createErrors.email ? " login-form__input--error" : ""}`}
                                placeholder="Enter your email address"
                            />
                        </div>
                        {createErrors.email && (
                            <p className="login-form__field-error">{createErrors.email}</p>
                        )}
                    </div>
                    <div className="auth-field">
                        <label className="auth-field__label" htmlFor="create-city">
                            Service location
                        </label>
                        <div
                            className={`auth-location-row${createErrors.city || createErrors.latitude || createErrors.longitude ? " auth-location-row--error" : ""}`}
                        >
                            <LocationAutocomplete
                                value={createValues.city}
                                onChange={handleCustomerCityChange}
                                onSelect={handleCustomerCitySelect}
                                onCommit={handleCustomerCityCommit}
                                placeholder="City, state"
                            />
                            <button
                                type="button"
                                className="auth-location-detect"
                                onClick={() => detectLocation()}
                                disabled={geoLoading || createLoading}
                            >
                                {geoLoading ? "Locating…" : "Use GPS"}
                            </button>
                        </div>
                        {createErrors.city && (
                            <p className="login-form__field-error">{createErrors.city}</p>
                        )}
                        {(createErrors.latitude || createErrors.longitude) && (
                            <p className="login-form__field-error">
                                {createErrors.latitude || createErrors.longitude}
                            </p>
                        )}
                        {geoMessage ? <p className="login-form__success">{geoMessage}</p> : null}
                        <p className="auth-field__hint">
                            Choose your service location (city/area) or use GPS so we can match caregivers near that location.
                        </p>
                    </div>
                    <div className="auth-field">
                        <label className="auth-field__label" htmlFor="create-lead-source">
                            How did you hear about us? <span className="auth-field__optional">(optional)</span>
                        </label>
                        <div className="auth-input-wrap">
                            <select
                                id="create-lead-source"
                                name="leadSource"
                                value={createValues.leadSource}
                                onChange={handleCreateChange}
                                className="login-form__input auth-select"
                            >
                                <option value="">Select a source</option>
                                {LEAD_SOURCE_OPTIONS.map((opt) => (
                                    <option key={opt.id} value={opt.id}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                                        <p className="auth-field__hint">
                        We will send a one-time code to verify your mobile. No password needed.
                    </p>
                </div>

                <div className="auth-step">
                    <div className="auth-step__head">
                        <span className="auth-step__num">2</span>
                        <h3 className="auth-step__title">Mobile Number</h3>
                    </div>
                    <div className="auth-field">
                        <div
                            className={`auth-phone-combo${createErrors.mobile ? " auth-phone-combo--error" : ""}`}
                        >
                            <span className="auth-phone-combo__code" aria-hidden="true">
                                <span className="auth-phone-combo__flag">🇮🇳</span>
                                +91
                            </span>
                            <div className="auth-phone-combo__body">
                                <div className="auth-input-wrap auth-phone-combo__input">
                                    <span className="auth-input-wrap__icon" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                                        </svg>
                                    </span>
                                    <input
                                        id={mobileId}
                                        name="mobile"
                                        type="tel"
                                        inputMode="numeric"
                                        maxLength={10}
                                        autoComplete="tel"
                                        value={createValues.mobile}
                                        onChange={handleCreateChange}
                                        className="login-form__input"
                                        placeholder="Enter your mobile number"
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="auth-send-otp"
                                    disabled={otpSending || (signupOtpSent && otpSecondsLeft > 0)}
                                    onClick={() => handleSendCustomerOtp({ hireMode: isHireFlow })}
                                >
                                    {otpSending
                                        ? "Sending…"
                                        : signupOtpSent && otpSecondsLeft <= 0
                                          ? "Resend"
                                          : "Send OTP"}
                                </button>
                            </div>
                        </div>
                        {createErrors.mobile && (
                            <p className="login-form__field-error">{createErrors.mobile}</p>
                        )}
                        <p className="auth-field__hint auth-field__hint--shield">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                            We&apos;ll send a one-time password to verify your number
                        </p>
                    </div>
                </div>

                <div className="auth-step">
                    <div className="auth-step__head">
                        <span className="auth-step__num">3</span>
                        <h3 className="auth-step__title">Verify Mobile Number</h3>
                    </div>
                    <div className="auth-field">
                        <div className="auth-otp-row auth-otp-row--digits">
                            <OtpDigitInput
                                id={otpId}
                                length={OTP_LENGTH}
                                value={otpCode}
                                onChange={(next) => {
                                    handleOtpCodeChange(setOtpCode, next);
                                    setCreateError("");
                                }}
                                disabled={!signupOtpSent}
                            />
                            {signupOtpSent && otpSecondsLeft > 0 ? (
                                <span className="auth-otp-timer auth-otp-timer--below" aria-live="polite">
                                    {formatOtpTimer(otpSecondsLeft)}
                                </span>
                            ) : null}
                        </div>
                        <p className="auth-field__hint">
                            Enter the {OTP_LENGTH}-digit code sent to your mobile.
                            {IS_DEV ? ` (Development test code: ${STUB_OTP})` : ""}
                        </p>
                        {otpMessage ? <p className="login-form__success">{otpMessage}</p> : null}
                    </div>
                </div>

                <div className="auth-step">
                    <div className="auth-step__head">
                        <span className="auth-step__num">4</span>
                        <h3 className="auth-step__title">You&apos;re Almost Done!</h3>
                    </div>

                    {createMessage && <p className="login-form__success">{createMessage}</p>}
                    {createError && (
                        <p className="login-form__error login-form__error--submit">{createError}</p>
                    )}

                    <button
                        type="submit"
                        className="auth-verify-btn"
                        disabled={createLoading || otpSending || !signupOtpSent}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <rect x="3" y="11" width="18" height="11" rx="2" />
                            <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                        {createLoading ? "Verifying…" : "Verify & Continue"}
                    </button>
                    <p className="auth-legal">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <rect x="3" y="11" width="18" height="11" rx="2" />
                            <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                        <span>
                            By continuing, you agree to our{" "}
                            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{" "}
                            <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                        </span>
                    </p>
                </div>
            </form>
        );
    }

    function renderCustomerRegisterForm() {
        if (customerHireOnboarding) {
            return (
                <CustomerHireOnboarding
                    embedded
                    requireBooking={isHireFlow}
                    customerEmail={createValues.email || ""}
                    initialSenior={{
                        address: hireRegistrationMeta?.city || "",
                    }}
                    searchContext={{
                        searchDate: searchParams.get("date"),
                        planType: searchParams.get("planType"),
                        searchShift: searchParams.get("searchShift"),
                        searchLocation: searchParams.get("searchLocation") || hireRegistrationMeta?.city || "",
                        latitude: createValues.latitude || searchParams.get("lat"),
                        longitude: createValues.longitude || searchParams.get("lng"),
                        hireCaregiverId: searchParams.get("hire"),
                    }}
                    onComplete={({ bookingId } = {}) => {
                        setCustomerHireOnboarding(false);
                        setHireRegistrationMeta(null);
                        if (!bookingId) {
                            navigate("/dashboard", {
                                replace: true,
                                state: {
                                    loginMessage:
                                        "Account created. Complete patient details from your dashboard when ready.",
                                },
                            });
                            return;
                        }
                        const dest = buildPostCustomerAuthNavigation(searchParams, {
                            loginMessage: "Account created. Continue browsing caregivers.",
                            bookingId,
                        });
                        navigate(dest.path, { replace: dest.replace !== false, state: dest.state });
                    }}
                />
            );
        }

        return renderCustomerRegistrationPopupForm();
    }

    function renderRoles() {
        const subMap = showCreate ? ROLE_SUB_CREATE : ROLE_SUB_SIGNIN;
        return (
            <div className="login-roles">
                {displayRoles.map((role) => (
                    <button
                        key={role.id}
                        type="button"
                        className={`login-role${selectedRole === role.id ? " login-role--active" : ""}`}
                        onClick={() => {
                            setSelectedRole(role.id);
                            setRoleError("");
                            resetSignupFlow();
                            syncAuthOverlayParams({ tab: "create", role: role.id });
                        }}
                    >
                        <span className="role-icon">{role.icon}</span>
                        <strong>{role.title}</strong>
                        <span>{subMap[role.id]}</span>
                    </button>
                ))}
            </div>
        );
    }

    function renderCaregiverPhoneFields({ mobileId = "cg-create-mobile" } = {}) {
        return (
            <>
                <div className="login-form__field">
                    <label className="login-form__label" htmlFor={mobileId}>
                        Mobile number <span className="required-star">*</span>
                    </label>
                    <input
                        id={mobileId}
                        name="mobile"
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={createValues.mobile}
                        onChange={handleCreateChange}
                        className={`login-form__input${createErrors.mobile ? " login-form__input--error" : ""}`}
                        placeholder="10-digit mobile (used to sign in)"
                        autoComplete="tel"
                    />
                    {createErrors.mobile ? (
                        <p className="login-form__field-error">{createErrors.mobile}</p>
                    ) : null}
                </div>
                <p className="auth-field__hint">
                    We will verify your mobile with a one-time code. After verification, the onboarding form starts and
                    you can enter profile details there.
                </p>
                <div className="auth-field">
                    <label className="auth-field__label" htmlFor="cg-create-lead-source">
                        How did you hear about us? <span className="auth-field__optional">(optional)</span>
                    </label>
                    <div className="auth-input-wrap">
                        <select
                            id="cg-create-lead-source"
                            name="leadSource"
                            value={createValues.leadSource}
                            onChange={handleCreateChange}
                            className="login-form__input auth-select"
                        >
                            <option value="">Select a source</option>
                            {CAREGIVER_LEAD_SOURCE_OPTIONS.map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <label className="terms-row">
                    <input
                        type="checkbox"
                        name="agreeTerms"
                        checked={createValues.agreeTerms}
                        onChange={handleCreateChange}
                    />
                    <span>
                        I agree to the{" "}
                        <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{" "}
                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                    </span>
                </label>
                {createErrors.agreeTerms ? (
                    <p className="login-form__field-error">{createErrors.agreeTerms}</p>
                ) : null}
            </>
        );
    }

    function renderAccountFields({ mobileId = "create-mobile", isCaregiver = false } = {}) {
        return (
            <>
                <div className="name-form">
                    <div className="login-form__field">
                        <label className="login-form__label" htmlFor="create-first-name">
                            First name <span className="required-star">*</span>
                        </label>
                        <input
                            id="create-first-name"
                            name="firstName"
                            type="text"
                            value={createValues.firstName}
                            onChange={handleCreateChange}
                            className={`login-form__input${createErrors.firstName ? " login-form__input--error" : ""}`}
                            placeholder="Priya"
                        />
                        {createErrors.firstName && (
                            <p className="login-form__field-error">{createErrors.firstName}</p>
                        )}
                    </div>
                    <div className="login-form__field">
                        <label className="login-form__label" htmlFor="create-last-name">
                            Last name
                        </label>
                        <input
                            id="create-last-name"
                            name="lastName"
                            type="text"
                            value={createValues.lastName}
                            onChange={handleCreateChange}
                            className="login-form__input"
                            placeholder="Reddy"
                        />
                    </div>
                </div>
                <div className="login-form__field">
                    <label className="login-form__label" htmlFor={mobileId}>
                        Mobile number <span className="required-star">*</span>
                    </label>
                    <input
                        id={mobileId}
                        name="mobile"
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={createValues.mobile}
                        onChange={handleCreateChange}
                        className={`login-form__input${createErrors.mobile ? " login-form__input--error" : ""}`}
                        placeholder="10-digit mobile (used to sign in)"
                    />
                    {createErrors.mobile && (
                        <p className="login-form__field-error">{createErrors.mobile}</p>
                    )}
                </div>
                {isCaregiver ? (
                    <>
                        <div className="login-form__field">
                            <label className="login-form__label" htmlFor="cg-create-service-role">
                                I am a <span className="required-star">*</span>
                            </label>
                            <select
                                id="cg-create-service-role"
                                name="serviceRole"
                                value={createValues.serviceRole}
                                onChange={handleCreateChange}
                                className={`login-form__input auth-select${createErrors.serviceRole ? " login-form__input--error" : ""}`}
                            >
                                {CAREGIVER_SIGNUP_SERVICE_ROLES.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                            {createErrors.serviceRole && (
                                <p className="login-form__field-error">{createErrors.serviceRole}</p>
                            )}
                        </div>
                    </>
                ) : null}
                <div className="login-form__field">
                    <label className="login-form__label">
                        Service location{" "}
                        {selectedRole === "manager" ? (
                            <span className="auth-field__optional">(optional)</span>
                        ) : (
                            <span className="required-star">*</span>
                        )}
                    </label>
                    <button
                        type="button"
                        className="login-form__otp-btn"
                        onClick={detectLocation}
                        disabled={geoLoading || createLoading}
                    >
                        {geoLoading ? "Fetching location…" : "Use my current location"}
                    </button>
                    <input name="latitude" type="hidden" value={createValues.latitude} readOnly />
                    <input name="longitude" type="hidden" value={createValues.longitude} readOnly />
                    {createErrors.latitude || createErrors.longitude ? (
                        <p className="login-form__field-error">
                            {createErrors.latitude || createErrors.longitude}
                        </p>
                    ) : null}
                    {geoMessage ? <p className="login-form__success">{geoMessage}</p> : null}
                </div>
                <p className="auth-field__hint">
                    We will verify your mobile with a one-time code. No password needed.
                </p>
                <label className="terms-row">
                    <input
                        type="checkbox"
                        name="agreeTerms"
                        checked={createValues.agreeTerms}
                        onChange={handleCreateChange}
                    />
                    <span>
                        I agree to the{" "}
                        <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{" "}
                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                    </span>
                </label>
                {createErrors.agreeTerms && (
                    <p className="login-form__field-error">{createErrors.agreeTerms}</p>
                )}
                {selectedRole === "manager" ? (
                    <>
                        <label className="terms-row">
                            <input
                                type="checkbox"
                                name="agreeCodeOfConduct"
                                checked={createValues.agreeCodeOfConduct}
                                onChange={handleCreateChange}
                            />
                            <span>I agree to the Care Manager Code of Conduct.</span>
                        </label>
                        {createErrors.agreeCodeOfConduct && (
                            <p className="login-form__field-error">{createErrors.agreeCodeOfConduct}</p>
                        )}
                    </>
                ) : null}
            </>
        );
    }

    function renderOtpStep({ purpose, onResend, backStep, inputId = "signup-otp", useDigitGrid = false }) {
        const mobile = normalizeIndianMobile(createValues.mobile);
        return (
            <>
                <p className="login-card__hint">
                    OTP sent to <strong>{mobile || "your mobile"}</strong>. Enter the {OTP_LENGTH}-digit code below.
                </p>
                <div className="login-form__field">
                    <label className="login-form__label" htmlFor={useDigitGrid ? `${inputId}-0` : inputId}>
                        Verification code
                    </label>
                    {useDigitGrid ? (
                        <OtpDigitInput
                            id={inputId}
                            length={OTP_LENGTH}
                            value={otpCode}
                            onChange={(next) => handleOtpCodeChange(setOtpCode, next)}
                            disabled={createLoading}
                        />
                    ) : (
                        <input
                            id={inputId}
                            className="login-form__input"
                            inputMode="numeric"
                            maxLength={OTP_LENGTH}
                            value={otpCode}
                            onChange={(e) =>
                                setOtpCode(sanitizeOtpInput(e.target.value, OTP_LENGTH))
                            }
                            placeholder={`${OTP_LENGTH}-digit code`}
                        />
                    )}
                    <button
                        type="button"
                        className="login-form__text-btn"
                        style={{ marginTop: "0.35rem" }}
                        disabled={otpSending}
                        onClick={() => onResend(purpose)}
                    >
                        {otpSending ? "Sending…" : "Resend OTP"}
                    </button>
                </div>
                {otpMessage ? <p className="login-form__success">{otpMessage}</p> : null}
                <button
                    type="button"
                    className="login-form__text-btn"
                    onClick={() => {
                        backStep();
                        setOtpCode("");
                        setOtpMessage("");
                        setCreateError("");
                    }}
                >
                    ← Back
                </button>
            </>
        );
    }

    function renderLoginOtpStep() {
        const mobile = loginOtpMobile || normalizeIndianMobile(loginValues.mobile);
        return (
            <>
                <p className="login-card__hint login-card__hint--step">
                    Step 2 of 2 — Verify mobile
                </p>
                <p className="login-card__hint">
                    OTP sent to <strong>{mobile || "your mobile"}</strong>. Enter the {OTP_LENGTH}-digit code below.
                </p>
                <div className="login-form__field">
                    <label className="login-form__label" htmlFor="login-otp-0">
                        Verification code
                    </label>
                    <OtpDigitInput
                        id="login-otp"
                        length={OTP_LENGTH}
                        value={otpCode}
                        onChange={(next) => handleOtpCodeChange(setOtpCode, next)}
                        disabled={loginLoading}
                    />
                    <button
                        type="button"
                        className="login-form__text-btn"
                        style={{ marginTop: "0.35rem" }}
                        disabled={otpSending}
                        onClick={resendLoginOtp}
                    >
                        {otpSending ? "Sending…" : "Resend OTP"}
                    </button>
                </div>
                {otpMessage ? <p className="login-form__success">{otpMessage}</p> : null}
                <button
                    type="button"
                    className="login-form__text-btn"
                    onClick={() => {
                        setLoginStep(1);
                        setOtpCode("");
                        setOtpMessage("");
                        setLoginError("");
                        setLoginOtpMobile("");
                    }}
                >
                    ← Back
                </button>
            </>
        );
    }

    function renderLoginForm() {
        const isOtpStep2 = loginStep === 2;
        const submitLabel = isOtpStep2
            ? loginLoading
                ? "Verifying…"
                : "Verify & sign in"
            : loginLoading
              ? "Sending OTP…"
              : isAdminLogin
                ? "Sign in"
                : "Send OTP";

        if (isOtpStep2) {
            return (
                <form onSubmit={handleLoginSubmit} className="login-form" noValidate>
                    <div className="auth-step">
                        <div className="auth-step__head">
                            <span className="auth-step__num">2</span>
                            <h3 className="auth-step__title">Verify Mobile Number</h3>
                        </div>
                        {renderLoginOtpStep()}
                    </div>
                    {loginMessage && (
                        <p className="login-form__success">{loginMessage} Redirecting…</p>
                    )}
                    {loginError && (
                        <p className="login-form__error login-form__error--submit">{loginError}</p>
                    )}
                    <button
                        type="submit"
                        className="auth-verify-btn"
                        disabled={loginLoading || otpSending || Boolean(loginMessage)}
                    >
                        {submitLabel}
                    </button>
                </form>
            );
        }

        return (
            <form onSubmit={handleLoginSubmit} className="login-form" noValidate>
                <div className="auth-step">
                    <div className="auth-step__head">
                        <span className="auth-step__num">1</span>
                        <h3 className="auth-step__title">
                            {isAdminLogin
                                ? "Admin sign in"
                                : selectedRole === "manager"
                                  ? "Care manager sign in"
                                  : "Sign in with mobile"}
                        </h3>
                    </div>
                    <div className="auth-field">
                        <label className="auth-field__label" htmlFor="login-mobile">
                            {isAdminLogin ? "Admin username" : "Mobile number"}
                        </label>
                        {isAdminLogin ? (
                            <div className="auth-input-wrap">
                                <input
                                    id="login-mobile"
                                    name="mobile"
                                    type="text"
                                    autoComplete="username"
                                    value={loginValues.mobile}
                                    onChange={handleLoginChange}
                                    onBlur={handleLoginBlur}
                                    className={`login-form__input${loginErrors.mobile ? " login-form__input--error" : ""}`}
                                    placeholder="Enter your admin username"
                                    aria-invalid={Boolean(loginErrors.mobile)}
                                />
                            </div>
                        ) : (
                        <div className="auth-mobile-row">
                            <span className="auth-mobile-row__code" aria-hidden="true">
                                🇮🇳 +91
                            </span>
                            <div className="auth-mobile-row__input auth-input-wrap">
                                <span className="auth-input-wrap__icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                                    </svg>
                                </span>
                                <input
                                    id="login-mobile"
                                    name="mobile"
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel username"
                                    value={loginValues.mobile}
                                    onChange={handleLoginChange}
                                    onBlur={handleLoginBlur}
                                    className={`login-form__input${loginErrors.mobile ? " login-form__input--error" : ""}`}
                                    placeholder="10-digit mobile used at signup"
                                    aria-invalid={Boolean(loginErrors.mobile)}
                                />
                            </div>
                        </div>
                        )}
                        {loginErrors.mobile ? (
                            <p className="login-form__field-error">{loginErrors.mobile}</p>
                        ) : (
                            <p className="auth-field__hint">
                                {isAdminLogin
                                    ? "Use your operations console username."
                                    : "Sign in with the mobile number you registered."}
                            </p>
                        )}
                    </div>
                    {isAdminLogin ? (
                    <div className="auth-field">
                        <div className="login-form__row">
                            <label className="auth-field__label" htmlFor="login-password">
                                Password
                            </label>
                        </div>
                        <div className="login-form__password-wrap">
                            <input
                                id="login-password"
                                name="password"
                                type={showLoginPassword ? "text" : "password"}
                                autoComplete="current-password"
                                value={loginValues.password}
                                onChange={handleLoginChange}
                                onBlur={handleLoginBlur}
                                className={`login-form__input login-form__input--with-toggle${loginErrors.password ? " login-form__input--error" : ""}`}
                                placeholder="Enter your password"
                                aria-invalid={Boolean(loginErrors.password)}
                            />
                            <PasswordVisibilityToggle
                                visible={showLoginPassword}
                                onToggle={() => setShowLoginPassword((prev) => !prev)}
                            />
                        </div>
                        {loginErrors.password && (
                            <p className="login-form__field-error">{loginErrors.password}</p>
                        )}
                    </div>
                    ) : (
                    <p className="auth-field__hint">
                        We will send a one-time verification code to your mobile. No password needed.
                    </p>
                    )}
                </div>

                {loginMessage && (
                    <p className="login-form__success">{loginMessage} Redirecting…</p>
                )}
                {loginError && (
                    <p className="login-form__error login-form__error--submit">{loginError}</p>
                )}

                <button
                    type="submit"
                    className="auth-verify-btn"
                    disabled={loginLoading || otpSending || Boolean(loginMessage)}
                >
                    {submitLabel}
                </button>
            </form>
        );
    }

    function renderCreateForm() {
        if (selectedRole === "customer") {
            return renderCustomerRegisterForm();
        }

        const isCaregiver = selectedRole === "caregiver";
        const isManager = selectedRole === "manager";
        const stepLabel = isCaregiver
            ? `Step ${caregiverSignupStep} of 2`
            : isManager
              ? `Step ${managerSignupStep} of ${MANAGER_SIGNUP_TOTAL_STEPS}`
              : null;

        let submitLabel = "Create account";
        if (isCaregiver) {
            if (caregiverSignupStep === 1) submitLabel = otpSending ? "Sending OTP…" : "Send OTP";
            else submitLabel = createLoading ? "Verifying…" : "Verify OTP & start onboarding";
        } else if (isManager) {
            if (managerSignupStep < MANAGER_SIGNUP_TOTAL_STEPS) {
                submitLabel = "Next Step →";
            } else if (!signupOtpSent) {
                submitLabel = otpSending ? "Sending OTP…" : "Submit Application";
            } else {
                submitLabel = createLoading ? "Submitting…" : "Submit Application";
            }
        } else if (createLoading) {
            submitLabel = "Creating account…";
        }

        return (
            <form onSubmit={handleCreateSubmit} className="login-form create-form" noValidate>
                {stepLabel ? (
                    <p className="login-card__hint" style={{ marginTop: 0 }}>
                        {isManager
                            ? `${SIGNUP_STEP_TITLES[managerSignupStep] || stepLabel}`
                            : `${stepLabel}${
                                  isCaregiver && caregiverSignupStep === 2 ? " — Verify mobile" : " — Mobile number"
                              }`}
                    </p>
                ) : (
                    <p className="login-card__hint" style={{ marginTop: 0 }}>
                        Your mobile number is your login ID for NEMA Care.
                    </p>
                )}

                {isCaregiver && caregiverSignupStep === 1 ? renderCaregiverPhoneFields({ mobileId: "cg-create-mobile" }) : null}
                {isCaregiver && caregiverSignupStep === 2
                    ? renderOtpStep({
                          purpose: "CAREGIVER_SIGNUP",
                          onResend: sendSignupOtp,
                          backStep: () => setCaregiverSignupStep(1),
                      })
                    : null}

                {isManager ? (
                    <CareManagerSignupFields
                        step={managerSignupStep}
                        createValues={createValues}
                        createErrors={createErrors}
                        onCreateFieldChange={handleCreateChange}
                        managerForm={managerForm}
                        onManagerPatch={patchManagerForm}
                        customArea={managerCustomArea}
                        onCustomAreaChange={setManagerCustomArea}
                        onAddCustomArea={addManagerCustomArea}
                        signupOtpSent={signupOtpSent}
                        otpCode={otpCode}
                        onOtpChange={setOtpCode}
                        otpDisabled={createLoading || otpSending}
                        termsError={managerTermsError}
                        onTermsAcceptedChange={(checked) => {
                            patchManagerForm({ termsAccepted: checked });
                            if (checked) setManagerTermsError("");
                        }}
                    />
                ) : null}

                {!isCaregiver && !isManager ? renderAccountFields({ mobileId: "cm-create-mobile" }) : null}

                {createMessage && <p className="login-form__success">{createMessage}</p>}
                {createError && (
                    <p className="login-form__error login-form__error--submit">{createError}</p>
                )}

                <div className={isManager ? "auth-cm-step-actions" : undefined}>
                    {isManager && managerSignupStep > 1 ? (
                        <button
                            type="button"
                            className="login-form__otp-btn"
                            onClick={() => {
                                setCreateError("");
                                setManagerSignupStep((s) => s - 1);
                            }}
                            disabled={createLoading || otpSending}
                        >
                            ← Back
                        </button>
                    ) : (
                        <span />
                    )}
                    <button
                        type="submit"
                        className={`login-form__submit create-form__submit${isManager && managerSignupStep === MANAGER_SIGNUP_TOTAL_STEPS ? " create-form__submit--success" : ""}`}
                        disabled={createLoading || otpSending}
                    >
                        {submitLabel}
                    </button>
                </div>
                {isCaregiver ? (
                    <p className="login-card__hint auth-existing-account">
                        Already have a caregiver account?{" "}
                        <button type="button" className="login-form__text-btn" onClick={() => switchTab(false)}>
                            Sign in
                        </button>
                    </p>
                ) : null}
            </form>
        );
    }

    const showCustomerRegisterPopup =
        !isAdminLogin && showCreate && selectedRole === "customer" && !customerHireOnboarding;

    const authModal = (
            <section
                className={`auth-modal${
                    showCreate || customerHireOnboarding || isAdminLogin
                        ? " auth-modal--expanded"
                        : " auth-modal--signin"
                }`}
                aria-label={showCreate ? "Create account" : "Sign in"}
            >
                <button
                    type="button"
                    className="auth-modal__close"
                    onClick={handleCloseAuth}
                    aria-label="Close"
                >
                    ×
                </button>

                {renderAuthTrustPanel()}

                <div className="auth-form-panel">
                    {!isAdminLogin && showCreate ? (
                        <div className="tab-switcher">
                            <button
                                type="button"
                                className={`tab-btn${showCreate ? "" : " active"}`}
                                onClick={() => switchTab(false)}
                            >
                                Sign In
                            </button>
                            <button
                                type="button"
                                className={`tab-btn${showCreate ? " active" : ""}`}
                                onClick={() => switchTab(true)}
                            >
                                Create Account
                            </button>
                        </div>
                    ) : null}

                    <div className="login-card">
                        {isAdminLogin ? (
                            <>
                                <p className="login-card__eyebrow">ADMIN PORTAL</p>
                                <h2>Operations sign in</h2>
                                <p className="login-card__hint">
                                    Sign in with your admin username and password to access the console.
                                </p>
                            </>
                        ) : showCustomerRegisterPopup || customerHireOnboarding ? (
                            <>
                                <p className="login-card__eyebrow">Join NEMA Care</p>
                                <h2>
                                    {customerHireOnboarding
                                        ? "Tell us about care needs"
                                        : "Create your account"}
                                </h2>
                                {customerHireOnboarding ? (
                                    <p className="login-card__hint">
                                        A brief patient profile helps us match the right caregivers.
                                    </p>
                                ) : null}
                            </>
                        ) : !showCreate ? (
                            <>
                                <p className="login-card__eyebrow">SECURE PORTAL</p>
                                <h2>Welcome back</h2>
                                <p className="login-card__hint">
                                    Enter your mobile and password to verify with OTP.
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="login-card__eyebrow">Join NEMA Care</p>
                                <h2>Create your account</h2>
                                {selectedRole === "caregiver" ? (
                                    <p className="login-card__hint">
                                        Register as a caregiver with a verified mobile number. Each mobile can
                                        only have one account type — use a different number if you already
                                        have a customer account.
                                    </p>
                                ) : selectedRole === "manager" ? (
                                    <p className="login-card__hint">
                                        Complete all five steps. Your mobile number is your login ID; OTP is verified on
                                        submit.
                                    </p>
                                ) : null}
                            </>
                        )}

                        {!isAdminLogin && isMaintenanceMode() ? (
                            <p className="login-form__error login-form__error--submit">
                                NEMA Care is under maintenance. Sign-in and registration may be limited until maintenance ends.
                            </p>
                        ) : null}

                        {showRolePicker ? renderRoles() : null}
                        {roleError ? (
                            <p className="login-form__error login-form__error--submit">{roleError}</p>
                        ) : null}

                        {showRolePicker && showCreate && !selectedRole ? (
                            <p className="login-card__hint">Select a role above to continue.</p>
                        ) : null}

                        {!isAdminLogin && showCreate && (selectedRole || isHireFlow) ? renderCreateForm() : null}
                        {!showCreate || isAdminLogin ? renderLoginForm() : null}

                        {!isAdminLogin && !showCustomerRegisterPopup && showCreate ? (
                        <p className="login-card__foot">
                            Already have an account?{" "}
                            <button type="button" onClick={() => switchTab(false)}>
                                Sign in →
                            </button>
                        </p>
                        ) : null}

                        <p className="auth-secure-foot">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                            Your information is 100% secure and will never be shared.
                        </p>
                    </div>
                </div>
            </section>
    );

    if (asOverlay) {
        const overlay = (
            <div className="auth-overlay" role="dialog" aria-modal="true" aria-label="Register to continue">
                <button
                    type="button"
                    className="auth-overlay__backdrop"
                    onClick={handleCloseAuth}
                    aria-label="Close sign in"
                />
                {authModal}
            </div>
        );
        return typeof document !== "undefined" ? createPortal(overlay, document.body) : overlay;
    }

    return (
        <main className={`page page--login${isHireFlow ? " page--login--hire" : ""}`}>
            {authModal}
        </main>
    );
}
